import Vue from 'vue';
import Component from 'vue-class-component';
import { Action, Getter, Mutation } from 'vuex-class';
import { StoreNamespace } from '@/globals/StoreNamespaceGlobal';
import { Grid } from '@/models/Grid.model';
import { USER_DETAILS } from '@/types/authentication.types';
import { UserDetails } from '@/models/UserDetails.model';
import { Prop } from 'vue-property-decorator';
import { GridColumn } from '@/models/GridColumn.model';
import {
  APPLICATION_USER_INFO_ERRORS,
  APPLICATION_USER_LOADING, APPLICATION_USER_RESET_PASSWORD,
  APPLICATION_USER_SEARCH_OBJECT,
  GET_APPLICATION_USER_INFO, GET_USER_CONTACT_DETAILS,
  SET_APPLICATION_USER_INFO_ERRORS,
  SET_APPLICATION_USER_LOADING,
  SET_APPLICATION_USER_SEARCH_OBJECT, SET_CURRENT_APP_USER_DETAILS, SET_OPEN_CHANGE_APP_USER_DETAILS_MODAL,
  SET_USER_ADDITIONAL_INFO_LIST,
  SET_USER_INFO_LIST, UNLOCK_USER, UPDATE_USER_CONTACT_DETAILS,
  USER_ADDITIONAL_INFO_LIST,
  USER_INFO_LIST,
} from '@/types/application-users.types';
import { ApplicationUserInfoModel } from '@/models/backoffice/ApplicationUserInfoModel';
import { ApplicationUserAdditionalInfoModel } from '@/models/backoffice/ApplicationUserAdditionalInfoModel';
import { ApplicationUserSearchModel } from '@/models/backoffice/ApplicationUserSearchModel';
import { FormActions } from '@/models/form/enum/FormActions';
import { Form101SearchType } from '@/models/manager/enums/Form101SearchType';
import { RowAction } from '@/models/RowAction.model';
import { ClickColumn } from '@/models/ClickColumn.model';
import { MenuAction } from '@/models/MenuAction.model';
import {
  BaseUserContactDetailsModel,
  UserContactDetailsModel,
} from '@/models/application-user/user-contact-details.model';

@Component({})
export default class ApplicationUserComponent extends Vue {

  @Action(GET_APPLICATION_USER_INFO, StoreNamespace.APPLICATION_USER) public getApplicationUserInfo;
  @Action(GET_USER_CONTACT_DETAILS, StoreNamespace.APPLICATION_USER)
  public getUserContactDetails: (userDetails: BaseUserContactDetailsModel) => void;
  @Action(UNLOCK_USER, StoreNamespace.APPLICATION_USER)
  public unlockUser: (userDetails: BaseUserContactDetailsModel) => void;
  @Action(APPLICATION_USER_RESET_PASSWORD, StoreNamespace.APPLICATION_USER)
  public applicationUserResetPassword: (userDetails: BaseUserContactDetailsModel) => void;

  @Mutation(SET_USER_INFO_LIST, StoreNamespace.APPLICATION_USER) public setUserInfoList;
  @Mutation(SET_USER_ADDITIONAL_INFO_LIST, StoreNamespace.APPLICATION_USER) public setUserAdditionalInfoList;
  @Mutation(SET_APPLICATION_USER_INFO_ERRORS, StoreNamespace.APPLICATION_USER) public setApplicationUserInfoErrors;
  @Mutation(SET_APPLICATION_USER_SEARCH_OBJECT, StoreNamespace.APPLICATION_USER) public setApplicationUserSearchObject;
  @Mutation(SET_APPLICATION_USER_LOADING, StoreNamespace.APPLICATION_USER) public setApplicationUserLoading;

  @Getter(USER_DETAILS, StoreNamespace.AUTHENTICATION) public userDetails!: UserDetails;
  @Getter(USER_INFO_LIST, StoreNamespace.APPLICATION_USER) public userInfoList!: Grid<ApplicationUserInfoModel>;
  @Getter(USER_ADDITIONAL_INFO_LIST, StoreNamespace.APPLICATION_USER)
  public userAdditionalInfoList!: Grid<ApplicationUserAdditionalInfoModel>;
  @Getter(APPLICATION_USER_INFO_ERRORS, StoreNamespace.APPLICATION_USER) public applicationUserInfoErrors!: string[];
  @Getter(APPLICATION_USER_SEARCH_OBJECT, StoreNamespace.APPLICATION_USER)
  public applicationUserSearchObject!: ApplicationUserSearchModel;
  @Getter(APPLICATION_USER_LOADING, StoreNamespace.APPLICATION_USER) public applicationUserLoading!: boolean;

  @Prop(String) public triggerSearch!: string;

  private userInfoColumns: GridColumn[] = [];
  private userAdditionalInfoColumns: GridColumn[] = [];
  private rowMenuActions: RowAction[] = [];
  private searched = false;
  private cleared = false;

  public created() {
    this.initUserInfoColumns();
    this.initUserAdditionalInfoColumns();
    this.initRowMenuActions();
    if (this.triggerSearch) {
      // this.$emit('fetchSearch', this.formsSearchObject);
    }
    this.$on('menuActionClicked', this.dynamicActionClicked);
  }

  public mounted() {
    this.setApplicationUserLoading(false);
    this.setApplicationUserInfoErrors([]);
    const me = this;
    if (this.triggerSearch) {
      setTimeout(() => {
        this.$emit('fetchSearch', this.applicationUserSearchObject);
      }, 100);
    }
    // window.onpopstate = (event) => {
    //   setTimeout(() => {
    //     me.$root.$emit('fetchSearch', me.applicationUserSearchObject);
    //   });
    // };
  }

  private initUserInfoColumns() {
    this.userInfoColumns.push({
      property: 'indexNum',
      header: 'application_user.index_num',
      hideSorting: true,
    } as GridColumn);
    this.userInfoColumns.push({
      property: 'indexName',
      header: 'application_user.index_name',
      hideSorting: true,
    } as GridColumn);
    this.userInfoColumns.push({
      property: 'fullName',
      header: 'application_user.full_name',
      hideSorting: true,
    } as GridColumn);
    this.userInfoColumns.push({
      property: 'locked',
      header: 'application_user.locked',
      hideSorting: true,
      booleanProperty: true,
      trueValue: '' + this.$t('application_user.yes'),
      falseValue: '' + this.$t('application_user.no'),
    } as GridColumn);
    this.userInfoColumns.push({
      property: 'temporaryLocked',
      header: 'application_user.temporaryLocked',
      hideSorting: true,
      booleanProperty: true,
      trueValue: '' + this.$t('application_user.yes'),
      falseValue: '' + this.$t('application_user.no'),
    } as GridColumn);
    this.userInfoColumns.push({
      property: 'externalUser',
      header: 'application_user.externalUser',
      hideSorting: true,
      booleanProperty: true,
      trueValue: '' + this.$t('application_user.external'),
      falseValue: '' + this.$t('application_user.internal'),
    } as GridColumn);
    this.userInfoColumns.push({
      property: 'lastSuccessfulLogin',
      header: 'application_user.lastSuccessfulLogin',
      hideSorting: true,
    } as GridColumn);
    this.userInfoColumns.push({
      property: 'dbName',
      header: 'application_user.db_name',
      hideSorting: true,
    } as GridColumn);
  }

  private initRowMenuActions() {
    this.rowMenuActions.push({
      name: 'CHANGE_USER_DETAILS',
      faIcon: ['fal', 'user-info'],
      event: FormActions.CHANGE_USER_DETAILS,
    } as RowAction);
    this.rowMenuActions.push({ name: 'RESET_PASSWORD', faIcon: ['fal', 'key'], event: 'RESET_PASSWORD' } as RowAction);
    this.rowMenuActions.push({ name: 'UNLOCK_USER', faIcon: ['fal', 'user'], event: 'UNLOCK_USER' } as RowAction);
  }

  private dynamicActionClicked(action: MenuAction, result: any) {
    switch (action.event) {
      case FormActions.CHANGE_USER_DETAILS:
        this.getUserContactDetails(result);
        break;
      case FormActions.UNLOCK_USER:
        this.unlockUser(result);
        break;
      case FormActions.RESET_PASSWORD:
        this.applicationUserResetPassword(result);
        break;
    }
  }

  private initUserAdditionalInfoColumns() {
    this.userAdditionalInfoColumns.push({
      property: 'errorLevel',
      header: 'application_user.error_level',
      classProperty: 'errorLevel',
      filterName: 'errorLevel',
      withFigure: true,
      hideSorting: true,
    } as GridColumn);
    this.userAdditionalInfoColumns.push({
      property: 'errorDesc',
      header: 'application_user.error_desc',
      hideSorting: true,
    } as GridColumn);
    this.userAdditionalInfoColumns.push({
      property: 'indexNum',
      header: 'application_user.index_num',
      hideSorting: true,
    } as GridColumn);
  }

  private updateSearchObject(newVal: any, property: string) {
    this.setApplicationUserSearchObject({ ...this.applicationUserSearchObject, [property]: newVal });
  }

  private onSearch(searchObject: any) {
    this.setApplicationUserLoading(true);
    this.setApplicationUserInfoErrors([]);
    this.getApplicationUserInfo();
  }

  private onClear(searchObject) {
    this.cleared = true;
    this.setApplicationUserInfoErrors([]);
    this.setUserInfoList({} as Grid);
    this.setUserAdditionalInfoList({} as Grid);
    this.setApplicationUserSearchObject({});
    setTimeout(() => {
      this.cleared = false;
    });
  }

  private beforeDestroy() {
    this.setApplicationUserInfoErrors([]);
    this.setUserInfoList({} as Grid);
    this.setUserAdditionalInfoList({} as Grid);
    this.setApplicationUserSearchObject({});
  }
}

